import { useEffect, useCallback, useMemo, useState, useRef } from 'react';
import { getAuth0Session } from '../network/endpoints/profile-pages/getAuth0Session';
import { ProfilePagesUpdateMessage, useUpdateBroadcastChannel } from '@mvecom/common/hooks/useUpdateBroadcastChannel';
import { getRetailUnitAndLanguage } from '../network/utils/getRetailUnitAndLanguage';
import { getAuthServiceUrl, toUrl } from '../utils/url';

export type Auth0Event =
  | { status: 'initial-auth-check-pending' }
  | { status: 'initial-auth-check-true' }
  | { status: 'initial-auth-check-false' }
  | { status: 'log-in-attempt-success' }
  | { status: 'log-out-attempt-success' };

export default function useAuth0(enabled: boolean, onAuthEvent: (event: Auth0Event) => void) {
  const { retailUnit, language: lang } = getRetailUnitAndLanguage();
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const onAuthEventRef = useRef(onAuthEvent);
  useEffect(() => {
    onAuthEventRef.current = onAuthEvent;
  }, [onAuthEvent, onAuthEventRef]);

  const logout = useCallback(() => {
    globalThis.location.href = toUrl(`${getAuthServiceUrl()}/logout`, {
      lang,
      retailUnit,
      referrer: globalThis.location.href,
    });
  }, [lang, retailUnit]);

  const login = useCallback(() => {
    globalThis.location.href = toUrl(`${getAuthServiceUrl()}/login`, {
      lang,
      retailUnit,
      referrer: globalThis.location.href,
    });
  }, [lang, retailUnit]);

  useUpdateBroadcastChannel(
    'profile_pages_update',
    retailUnit,
    useCallback(
      (message: ProfilePagesUpdateMessage) => {
        if (!enabled) {
          return;
        }
        if (message.action === 'signed-in') {
          onAuthEventRef.current({ status: 'log-in-attempt-success' });
          setLoggedIn(true);
        }

        if (message.action === 'signed-out') {
          onAuthEventRef.current({ status: 'log-out-attempt-success' });
          setLoggedIn(false);
        }
      },
      [onAuthEventRef, enabled],
    ),
  );

  // initial auth check
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const getSessionStatus = async (abortController: AbortController) => {
      onAuthEventRef.current({ status: 'initial-auth-check-pending' });

      try {
        const sessionStatus = await getAuth0Session(abortController.signal);

        if (!abortController.signal.aborted) {
          setLoggedIn(sessionStatus.isAuthenticated);
          onAuthEventRef.current({
            status: sessionStatus.isAuthenticated ? 'initial-auth-check-true' : 'initial-auth-check-false',
          });
        }
      } catch (error) {
        console.error(error);
        if (!abortController.signal.aborted) {
          onAuthEventRef.current({ status: 'initial-auth-check-false' });
        }
      }
    };

    const abortController = new AbortController();
    getSessionStatus(abortController);

    return () => abortController.abort();
  }, [enabled, onAuthEventRef]);

  return useMemo(() => ({ loggedIn, login, logout }), [loggedIn, login, logout]);
}
